import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faCaretLeft,
  faCaretRight,
  faDharmachakra,
  faKey,
  faLocationDot,
  faPhone,
  faTableCellsLarge,
  faTruck,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { useLocalStorage } from 'usehooks-ts';
import {
  AssignmentSortFieldEnum,
  AssignmentStatusEnum,
  CaseTypeEnum,
  DirectionEnum,
  LoadingStatusEnum,
} from 'api/model';
import {
  useAssignmentGetCarTransportTruckAssignmentsForAssignmentHook,
  useAssignmentSearchAssignmentWithPagingHook,
} from 'api/assignment/assignment';
import { useApiCallFn } from 'hooks/useApiCallFn';
import useCompanies from 'contexts/basicData/useCompanies';
import useTranslations from 'contexts/basicData/useTranslations';

/**
 * Startklar — always-on monitor board ("Förberedelseskärm") showing today's
 * booked-but-not-yet-inspected assignments so facility staff can prep cars
 * (wash, fuel/charge, key cabinet) before the field tester arrives. Cards stay
 * on the board until the FT has inspected the car (not merely started the
 * assignment).
 *
 * The board is designed at a fixed 1280×720 canvas that is scaled to fill any
 * monitor (letterboxed on black), so proportions stay identical on every TV.
 */

const REFRESH_MS = 60_000;

/** Kortmått från klassiska 1280×720-layouten (2 rader × 4 kolumner). */
const CARD_W = 296;
const CARD_H = 277;
const GRID_GAP = 12;
/** Rutnätets sidopadding i canvasen (30 px per sida). */
const GRID_SIDE_PAD = 60;
/** Header + toolbar + rutnätets paddings — allt ovanför/under korten. */
const CHROME_H = 154;
/**
 * Canvasen växer med antal kortrader men håller alltid 16:9 — bredden följer
 * höjden och kolumnantalet blir så många kort som ryms (2 rader = exakt
 * klassiska 1280×720 med 4 kolumner). Fit-skalningen letterboxar som vanligt,
 * så fler rader = mindre men fler kort på samma skärm — tänkt för större TV.
 */
const canvasHeight = (cardRows: number) =>
  CHROME_H + cardRows * CARD_H + (cardRows - 1) * GRID_GAP;

const canvasWidth = (canvasH: number) => Math.round((canvasH * 16) / 9);

const gridCols = (canvasW: number) =>
  Math.max(
    1,
    Math.floor((canvasW - GRID_SIDE_PAD + GRID_GAP) / (CARD_W + GRID_GAP))
  );

const MIN_CARD_ROWS = 2;
const MAX_CARD_ROWS = 4;

const BOARD_BG = '#1b1c1e';
const CARD_BG = '#26282b';
const BRAND_ORANGE = '#cc6633';

const ALL_FACILITIES = 'Alla';

/**
 * Syntetiskt dropdown-val som slår ihop alla anläggningar på Kalkstensgatan
 * 21A/21B i Strängnäs — flera from-namn, samma fysiska plats.
 */
const STRANGNAS_GROUP = 'Strängnäs';

const isStrangnasAddress = (address: string) => {
  const a = address.toLowerCase().replaceAll(' ', '');
  return a.startsWith('kalkstensgatan21a') || a.startsWith('kalkstensgatan21b');
};

/**
 * The board only concerns cars prepped at a facility: Hemleverans,
 * Butiksleverans och Verkstadsbilar. Flytt mellan anläggningar
 * (FacilityTransfer) is deliberately left out.
 */
const BOARD_CASE_TYPES = [
  CaseTypeEnum.HomeDelivery,
  CaseTypeEnum.FacilityToFacility,
  CaseTypeEnum.Verkstadsbilar,
];

/**
 * Assignment has not been inspected by the FT yet (and is not canceled) —
 * kortet ska visas tills FT:n har inspekterat bilen, inte bara påbörjat
 * uppdraget (Started/LoadedCars är alltså kvar på tavlan).
 * "Tilldelad till lastbil" är kvar i urvalet — statusen sätts redan när bilen
 * tilldelas lastbilen, men den ska förberedas tills den faktiskt är lastad.
 * Fysiskt lastade bilar filtreras bort separat via LoadingStatus.
 */
const UNINSPECTED_STATUSES: AssignmentStatusEnum[] = [
  AssignmentStatusEnum.Created,
  AssignmentStatusEnum.OnHold,
  AssignmentStatusEnum.Planned,
  AssignmentStatusEnum.AssignedToCarTransportTruck,
  AssignmentStatusEnum.Assigned,
  AssignmentStatusEnum.Accepted,
  AssignmentStatusEnum.Started,
  AssignmentStatusEnum.LoadedCars,
];

/**
 * FT:n har inspekterat bilen (eller senare) — den behöver inte längre
 * förberedas. Räknas i "Klara"-countern. Avbokade/avbrutna/skrotade räknas
 * inte alls.
 */
const INSPECTED_STATUSES: AssignmentStatusEnum[] = [
  AssignmentStatusEnum.Inspected,
  AssignmentStatusEnum.StartedDriving,
  AssignmentStatusEnum.Arrived,
  AssignmentStatusEnum.TestComplete,
  AssignmentStatusEnum.Returning,
  AssignmentStatusEnum.FinishedDriving,
  AssignmentStatusEnum.UnLoadedCars,
  AssignmentStatusEnum.Complete,
  AssignmentStatusEnum.Approved,
];

type UrgencyKey = 'over' | 'now' | 'soon' | 'later';

const URGENCY: Record<UrgencyKey, { color: string; accent: string }> = {
  over: { color: '#9aa0a6', accent: '#cc2d30' },
  now: { color: '#ff6a4d', accent: '#ff5f1f' },
  soon: { color: '#ffbf00', accent: '#ffbf00' },
  later: { color: '#7fd1a3', accent: '#3a7d54' },
};

/** Nästa dags uppdrag — blå utfyllnad när dagens börjar ta slut. */
const NEXT_DAY = { color: '#8ab4e8', accent: '#3d6ea8' };

function urgency(mins: number): UrgencyKey {
  if (mins < 0) return 'over';
  if (mins <= 5) return 'now';
  if (mins <= 15) return 'soon';
  return 'later';
}

interface BoardRow {
  assignmentId: number;
  status: AssignmentStatusEnum;
  /** Tillhör dagen efter vald dag — visas som blå utfyllnad. */
  nextDay: boolean;
  regnr: string;
  modell: string;
  typ: string;
  facility: string;
  facilityAddress: string;
  till: string;
  /** Framme-tid hos kunden = BookedTo (mötestiden). */
  framme: Date;
  /** Fälttestaren som ska köra uppdraget (namn + telefon). */
  ft: string;
  ftTel: string;
  bokare: string;
  bokareTel: string;
  /** "Nyckel inlämnad" — visas som nyckel-badge på kortet. */
  keyHandedIn: boolean;
  /**
   * "Ska på lastbil" på just detta ben (Assignment.ShouldGoOnTruck). Kortet ÄR benet från
   * anläggningen, så ett senare bens flagga på ett uppdelat ärende syns aldrig här.
   */
  shouldGoOnTruck: boolean;
  foretag: string;
  info: string;
  startklar: Date;
}

/**
 * Lastbils-badgen gäller BENET på kortet, dvs. uppdraget från den valda anläggningen:
 * antingen är just det benet bockat "Ska på lastbil", eller så är det redan planerat på en
 * lastbil (statusen sätts när benet lastas — flaggan nollställs då, så statusen tar över).
 * Ett uppdelat ärende där först ett senare ben ska på lastbil får alltså ingen badge på
 * kortet för det första, individuellt körda benet.
 */
const showTruckBadge = (r: BoardRow) =>
  r.shouldGoOnTruck ||
  r.status === AssignmentStatusEnum.AssignedToCarTransportTruck;

// --- Tid & format ---

const pad2 = (n: number) => String(n).padStart(2, '0');

const fmtClock = (d: Date) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

function fmtDateLong(d: Date): string {
  const s = d.toLocaleDateString('sv-SE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function fmtDateShort(d: Date): string {
  const s = d.toLocaleDateString('sv-SE', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const minsLeft = (startklar: Date, now: Date) =>
  Math.round((startklar.getTime() - now.getTime()) / 60_000);

/** Från 2 h och uppåt (t.ex. andra datum) blir råa minuter oläsliga. */
function leftLabel(mins: number): { big: string; small: string } {
  const abs = Math.abs(mins);
  if (mins < 0) {
    return {
      big: 'Försenat',
      small:
        abs >= 120
          ? `${Math.floor(abs / 60)} h sedan`
          : `${abs} min sedan`,
    };
  }
  if (mins === 0) return { big: 'Nu', small: 'startklar nu' };
  if (mins >= 120) return { big: `${Math.floor(mins / 60)} h`, small: 'kvar' };
  return { big: `${mins} min`, small: 'kvar' };
}

function useNow(intervalMs: number): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

/** Scale factor that letterboxes the fixed canvas onto the current window. */
function useFitScale(canvasW: number, canvasH: number): number {
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const fit = () =>
      setScale(
        Math.min(window.innerWidth / canvasW, window.innerHeight / canvasH)
      );
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [canvasW, canvasH]);
  return scale;
}

// --- Styled components ---

const Stage = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
`;

const Canvas = styled.div<{ scale: number; canvasW: number; canvasH: number }>`
  flex: none;
  width: ${({ canvasW }) => canvasW}px;
  height: ${({ canvasH }) => canvasH}px;
  transform: scale(${({ scale }) => scale});
  transform-origin: center center;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${BOARD_BG};
  color: #fff;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 28px;
  padding: 15px 30px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 0 0 auto;
`;

const Logo = styled.img`
  height: 34px;
  display: block;
  filter: brightness(0) invert(1);
  opacity: 0.96;
`;

const TitleWrap = styled.div`
  border-left: 1px solid rgba(255, 255, 255, 0.18);
  padding-left: 16px;
`;

const Title = styled.div`
  font-size: 26px;
  font-weight: 800;
  color: #fff;
  line-height: 1;
  letter-spacing: -0.01em;
`;

const Counters = styled.div`
  display: flex;
  gap: 30px;
  margin-left: 10px;
`;

const CounterN = styled.div<{ tone?: string }>`
  font-size: 30px;
  font-weight: 800;
  line-height: 1;
  color: ${({ tone }) => tone ?? '#fff'};
`;

const CounterL = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 3px;
  white-space: nowrap;
`;

const ClockWrap = styled.div`
  margin-left: auto;
  text-align: right;
`;

const Clock = styled.div`
  font-size: 40px;
  font-weight: 800;
  color: #fff;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
`;

const ClockSeconds = styled.span`
  font-size: 21px;
  color: rgba(255, 255, 255, 0.45);
  font-weight: 700;
`;

const DateLine = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.55);
  margin-top: 5px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 12px 30px 4px;
`;

const Place = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
`;

const FacilitySelect = styled.select`
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  background: transparent;
  border: none;
  cursor: pointer;
  outline: none;

  option {
    color: #111;
  }
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  min-width: 0;
`;

const DateNav = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const DateArrow = styled.button`
  font: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const DateNavLabel = styled.span<{ offDay: boolean }>`
  font-size: 15px;
  font-weight: 700;
  color: ${({ offDay }) => (offDay ? '#ffbf00' : '#fff')};
  white-space: nowrap;
  min-width: 90px;
  text-align: center;
`;

const TodayButton = styled.button`
  font: inherit;
  font-size: 12.5px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 999px;
  border: 1px solid ${BRAND_ORANGE};
  background: ${BRAND_ORANGE};
  color: #fff;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    opacity: 0.85;
  }
`;

const Legend = styled.div`
  display: flex;
  gap: 18px;
  flex: none;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
`;

const LegendSwatch = styled.span<{ color: string; dim?: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${({ color }) => color};
  opacity: ${({ dim }) => (dim ? 0.55 : 1)};
`;

const Grid = styled.div<{ cardCols: number; cardRows: number }>`
  flex: 1;
  display: grid;
  grid-template-columns: repeat(${({ cardCols }) => cardCols}, 1fr);
  grid-template-rows: repeat(${({ cardRows }) => cardRows}, 1fr);
  gap: ${GRID_GAP}px;
  padding: 8px 30px 16px;
  min-height: 0;
`;

const Card = styled.div<{ accent: string; over: boolean; glow: boolean }>`
  background: ${CARD_BG};
  border-radius: 10px;
  border-left: 6px solid ${({ accent }) => accent};
  padding: 11px 15px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  opacity: ${({ over }) => (over ? 0.48 : 1)};
  box-shadow: ${({ glow, accent }) =>
    glow
      ? `0 0 0 1px ${accent}55, 0 2px 8px rgba(0, 0, 0, 0.35)`
      : '0 1px 3px rgba(0, 0, 0, 0.3)'};
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const TypePill = styled.span`
  font-size: 12px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.78);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Modell = styled.div`
  font-size: 17px;
  font-weight: 700;
  line-height: 1.2;
  color: #fff;
  margin-top: 5px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Meta = styled.div`
  margin-top: 5px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
`;

const RouteTo = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.82);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RouteFrom = styled.span`
  color: rgba(255, 255, 255, 0.55);
  font-weight: 500;
`;

/** Fyller ikonens plats på destinationsraden så från/till-raderna linjerar. */
const RouteArrowSlot = styled.span`
  flex: none;
  width: 14px;
  text-align: center;
  color: ${BRAND_ORANGE};
  font-weight: 800;
`;

const ArrivalTime = styled.span`
  flex: none;
  font-size: 13px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.65);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const SubIcon = styled(FontAwesomeIcon)`
  flex: none;
  width: 14px;
`;

const SubText = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InfoRow = styled.div`
  font-size: 12.5px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-left: 21px;
`;

/** Sitter i nedre högra hörnet av kortet, ovanför CardBottom-strecket. */
const BadgeRow = styled.span`
  align-self: flex-end;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
`;

const TruckBadge = styled.span`
  align-self: flex-end;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 700;
  padding: 3px 8px;
  margin-top: 2px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.75);
  white-space: nowrap;
`;

const CardBottom = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-top: 6px;
  padding-top: 7px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const StartklarCol = styled.div`
  display: flex;
  flex-direction: column;
`;

const StartklarLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const StartklarTime = styled.span`
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  font-variant-numeric: tabular-nums;
  margin-top: 2px;
`;

const LeftCol = styled.div<{ color: string }>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  color: ${({ color }) => color};
`;

const LeftBig = styled.span`
  font-size: 27px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
`;

const LeftSmall = styled.span`
  font-size: 11.5px;
  font-weight: 600;
  opacity: 0.8;
  margin-top: 2px;
`;

const OverflowCell = styled.div`
  border: 2px dashed rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: rgba(255, 255, 255, 0.45);
  font-size: 22px;
  font-weight: 800;

  span:last-child {
    font-size: 12.5px;
    font-weight: 600;
  }
`;

const EmptyAll = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.35);
  font-size: 22px;
  font-weight: 600;
`;

// --- Svensk registreringsskylt ---

const PlateWrap = styled.span`
  display: inline-flex;
  align-items: stretch;
  height: 34px;
  background: #fff;
  color: #111;
  border-radius: 7px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.25);
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.15);
  flex: none;
`;

const PlateEuBand = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #003399;
  color: #fff;
  width: 16px;
  gap: 1px;

  span:first-child {
    font-size: 8px;
    line-height: 1;
    color: #ffcc00;
    font-weight: 700;
  }

  span:last-child {
    font-size: 11px;
    line-height: 1;
    font-weight: 800;
  }
`;

const PlateNumber = styled.span`
  display: flex;
  align-items: center;
  padding: 0 13px;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: 0.03em;
  white-space: nowrap;
`;

const Plate: React.FC<{ value: string }> = ({ value }) => (
  <PlateWrap>
    <PlateEuBand>
      <span>★</span>
      <span>S</span>
    </PlateEuBand>
    <PlateNumber>{value}</PlateNumber>
  </PlateWrap>
);

// --- Page ---

const StartklarPage: React.FC = () => {
  const now = useNow(1000);
  const { caseTypes } = useTranslations();
  const { companiesById } = useCompanies();
  const [facility, setFacility] = useLocalStorage<string>(
    'startklar.facility',
    ALL_FACILITIES
  );
  const [cardRows, setCardRows] = useLocalStorage<number>('startklar.rows', 2);
  const safeCardRows = Math.min(
    MAX_CARD_ROWS,
    Math.max(MIN_CARD_ROWS, cardRows)
  );
  const canvasH = canvasHeight(safeCardRows);
  const canvasW = canvasWidth(canvasH);
  const cardCols = gridCols(canvasW);
  const scale = useFitScale(canvasW, canvasH);
  const maxCards = cardCols * safeCardRows;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Zäkra Go — Dagens bilar';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  // Valt datum som offset från idag, så att en monitor som står kvar på
  // "idag" automatiskt rullar över till nya dagen vid midnatt.
  const [dayOffset, setDayOffset] = useState(0);
  const todayKey = now.toDateString();
  const selectedDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + dayOffset);
    return d;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayKey, dayOffset]);
  const isToday = dayOffset === 0;

  const searchAssignmentWithPaging =
    useAssignmentSearchAssignmentWithPagingHook();
  const assignmentsCall = useApiCallFn((date: Date) => {
    // Även dagen efter hämtas, som blå utfyllnad när dagens uppdrag tar slut.
    const dayAfter = new Date(date);
    dayAfter.setDate(dayAfter.getDate() + 1);
    return searchAssignmentWithPaging({
      startDate: date,
      endDate: dayAfter,
      caseTypes: [...BOARD_CASE_TYPES],
      // Inspekterade hämtas med för "Klara"-countern; korten filtreras client-side.
      assignmentStatuses: [...UNINSPECTED_STATUSES, ...INSPECTED_STATUSES],
      orderByField: AssignmentSortFieldEnum.BookedTo,
      direction: DirectionEnum.Ascending,
      forCurrentUser: false,
      size: 500,
      skip: 0,
    });
  });

  useEffect(() => {
    // Rensa förra dagens svar så att kort från fel datum inte blinkar förbi.
    assignmentsCall.setResponse(undefined);
    assignmentsCall.run(selectedDate);
    const t = setInterval(() => {
      assignmentsCall.run(selectedDate);
    }, REFRESH_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const allRows = useMemo((): BoardRow[] => {
    const nextMidnight = new Date(selectedDate);
    nextMidnight.setDate(nextMidnight.getDate() + 1);
    return (assignmentsCall.response?.result ?? []).map((av) => {
      // Även bilar tilldelade lastbil visar sitt eget uppdrags estimerade
      // starttid — det är när bilen måste stå klar på anläggningen. (De
      // räknas som klara först när de fysiskt lastats, se isKlar nedan.)
      const startklar =
        av.assignment.estimatedStartTime ?? av.assignment.bookedTo;
      return {
        assignmentId: av.assignment.assignmentID,
        status: av.assignment.assignmentStatusID,
        // Klassas på tiden som visas på kortet, så "Nästa dag" aldrig
        // står framför ett klockslag som är idag.
        nextDay: startklar >= nextMidnight,
        regnr: av.case.registrationNumber,
        // Märket ingår nästan alltid redan i modellnamnet ("Volvo V90"),
        // så det visas bara när modell saknas helt.
        modell: av.case.vehicleModel || av.case.vehicleBrand || '',
        typ: caseTypes[av.case.caseTypeID] ?? '',
        facility: av.assignment.fromName,
        facilityAddress: av.assignment.fromAddress,
        // Orten, inte mottagarens namn — toName är kundens namn på hemleveranser.
        till: av.assignment.toCity || av.assignment.toName,
        // Framme hos kunden = BookedTo — mötestiden hos kunden.
        framme: av.assignment.bookedTo,
        ft: av.assignment.assignedTo?.name ?? '',
        ftTel: av.assignment.assignedTo?.phoneNumber ?? '',
        bokare: av.case.bookerName,
        bokareTel: av.case.bookerPhoneNumber,
        keyHandedIn: av.assignment.keyHandedIn === true,
        shouldGoOnTruck: av.assignment.shouldGoOnTruck === true,
        foretag:
          (av.case.companyID != null &&
            companiesById.get(av.case.companyID)?.name) ||
          '',
        info: av.assignment.extraInfo,
        // Estimerad starttid = när FT:n väntas påbörja uppdraget — det är
        // då bilen måste stå klar. Utan estimat faller vi tillbaka på bookedTo.
        startklar,
      };
    });
  }, [
    assignmentsCall.response?.result,
    caseTypes,
    companiesById,
    selectedDate,
  ]);

  // "Tilldelad till lastbil" betyder bara att bilen är planerad på en lastbil.
  // Klar för tavlan är den först när den fysiskt är lastad (eller redan avlastad
  // på destinationen) — det syns bara i LoadingStatus, som hämtas per uppdrag.
  const getCarTransportTruckAssignmentsForAssignment =
    useAssignmentGetCarTransportTruckAssignmentsForAssignmentHook();
  const loadedCheckCall = useApiCallFn(async (ids: number[]) => {
    const results = await Promise.all(
      ids.map(async (id) => ({
        id,
        trucks: await getCarTransportTruckAssignmentsForAssignment({
          assignmentId: id,
        }),
      }))
    );
    return results
      .filter((r) =>
        r.trucks.some((t) => t.loadingStatus !== LoadingStatusEnum.NotLoaded)
      )
      .map((r) => r.id);
  });

  useEffect(() => {
    const truckAssignedIds = allRows
      .filter(
        (r) => r.status === AssignmentStatusEnum.AssignedToCarTransportTruck
      )
      .map((r) => r.assignmentId);
    if (truckAssignedIds.length > 0) {
      loadedCheckCall.run(truckAssignedIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows]);

  const loadedOnTruckIds = loadedCheckCall.response;

  const facilityOptions = useMemo(() => {
    const counts = new Map<string, number>();
    allRows.forEach((r) => {
      if (r.facility)
        counts.set(r.facility, (counts.get(r.facility) ?? 0) + 1);
    });
    const strangnasCount = allRows.filter((r) =>
      isStrangnasAddress(r.facilityAddress)
    ).length;
    if (strangnasCount > 0) counts.set(STRANGNAS_GROUP, strangnasCount);
    const options = Array.from(counts.keys()).sort(
      (a, b) =>
        (counts.get(b) ?? 0) - (counts.get(a) ?? 0) || a.localeCompare(b, 'sv')
    );
    // Keep the persisted choice selectable even on days it has no assignments.
    if (facility !== ALL_FACILITIES && !counts.has(facility))
      options.push(facility);
    return [ALL_FACILITIES, ...options];
  }, [allRows, facility]);

  const { rows, nextDayRows, klaraCount } = useMemo(() => {
    // Klar = FT:n har inspekterat bilen, eller bilen är fysiskt lastad på
    // lastbil. Ska inte längre förberedas.
    const isKlar = (r: BoardRow) =>
      INSPECTED_STATUSES.includes(r.status) ||
      (r.status === AssignmentStatusEnum.AssignedToCarTransportTruck &&
        (loadedOnTruckIds?.includes(r.assignmentId) ?? false));
    const inFacility = allRows.filter((r) =>
      facility === ALL_FACILITIES
        ? true
        : facility === STRANGNAS_GROUP
          ? isStrangnasAddress(r.facilityAddress)
          : r.facility === facility
    );
    const byStartklar = (a: BoardRow, b: BoardRow) =>
      a.startklar.getTime() - b.startklar.getTime();
    const prep = inFacility.filter((r) => !isKlar(r));
    return {
      rows: prep.filter((r) => !r.nextDay).sort(byStartklar),
      nextDayRows: prep.filter((r) => r.nextDay).sort(byStartklar),
      // Räknarna gäller vald dag — nästa dags rader är bara utfyllnad.
      klaraCount: inFacility.filter((r) => !r.nextDay && isKlar(r)).length,
    };
  }, [allRows, facility, loadedOnTruckIds]);

  const urgentCount = rows.filter((r) => {
    const mins = minsLeft(r.startklar, now);
    return mins >= 0 && mins <= 15;
  }).length;
  const lateCount = rows.filter((r) => minsLeft(r.startklar, now) < 0).length;

  // Nästa dags kort fyller bara tomma platser — de tränger aldrig undan
  // dagens kort. Ryms inte allt blir sista platsen en overflow-cell.
  const totalPrep = rows.length + nextDayRows.length;
  const slots = totalPrep > maxCards ? maxCards - 1 : maxCards;
  const visibleToday = rows.slice(0, slots);
  const visibleNext = nextDayRows.slice(
    0,
    Math.max(0, slots - visibleToday.length)
  );
  const visibleRows = [...visibleToday, ...visibleNext];
  const overflowCount = totalPrep - visibleRows.length;
  const overflowNextDayOnly = rows.length === visibleToday.length;

  return (
    <Stage>
      <Canvas canvasH={canvasH} canvasW={canvasW} scale={scale}>
        <Header>
          <HeaderLeft>
            <Logo alt="Zäkra Go" src={`${process.env.PUBLIC_URL}/Zakra_Logo.svg`} />
            <TitleWrap>
              <Title>Dagens bilar</Title>
            </TitleWrap>
          </HeaderLeft>

          <Counters>
            <div>
              <CounterN tone="#7fd1a3">{klaraCount}</CounterN>
              <CounterL>Klara</CounterL>
            </div>
            <div>
              <CounterN>{rows.length}</CounterN>
              <CounterL>Att förbereda</CounterL>
            </div>
            <div>
              <CounterN tone="#ffbf00">{urgentCount}</CounterN>
              <CounterL>Inom 15 min</CounterL>
            </div>
            <div>
              <CounterN tone="#ff6a4d">{lateCount}</CounterN>
              <CounterL>Försenade</CounterL>
            </div>
          </Counters>

          <ClockWrap>
            <Clock>
              {fmtClock(now)}
              <ClockSeconds>:{pad2(now.getSeconds())}</ClockSeconds>
            </Clock>
            <DateLine>{fmtDateLong(now)}</DateLine>
          </ClockWrap>
        </Header>

        <Toolbar>
          <ToolbarLeft>
            <Place>
              <FontAwesomeIcon
                color={BRAND_ORANGE}
                fontSize={14}
                icon={faBuilding}
              />
              <FacilitySelect
                onChange={(e) => setFacility(e.target.value)}
                value={facility}
              >
                {facilityOptions.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </FacilitySelect>
            </Place>
            <DateNav>
              <DateArrow
                onClick={() => setDayOffset((o) => o - 1)}
                title="Föregående dag"
              >
                <FontAwesomeIcon icon={faCaretLeft} />
              </DateArrow>
              <DateNavLabel offDay={!isToday}>
                {isToday ? 'Idag' : fmtDateShort(selectedDate)}
              </DateNavLabel>
              <DateArrow
                onClick={() => setDayOffset((o) => o + 1)}
                title="Nästa dag"
              >
                <FontAwesomeIcon icon={faCaretRight} />
              </DateArrow>
              {!isToday && (
                <TodayButton onClick={() => setDayOffset(0)}>
                  Idag
                </TodayButton>
              )}
            </DateNav>
            <Place>
              <FontAwesomeIcon
                color={BRAND_ORANGE}
                fontSize={14}
                icon={faTableCellsLarge}
              />
              <FacilitySelect
                onChange={(e) => setCardRows(Number(e.target.value))}
                value={safeCardRows}
              >
                {[2, 3, 4].map((n) => (
                  <option key={n} value={n}>
                    {n} rader
                  </option>
                ))}
              </FacilitySelect>
            </Place>
          </ToolbarLeft>
          <Legend>
            <LegendItem>
              <LegendSwatch color={URGENCY.now.accent} />≤ 5 min
            </LegendItem>
            <LegendItem>
              <LegendSwatch color={URGENCY.soon.accent} />≤ 15 min
            </LegendItem>
            <LegendItem>
              <LegendSwatch color={URGENCY.later.accent} />
              {isToday ? 'senare idag' : 'senare'}
            </LegendItem>
            <LegendItem>
              <LegendSwatch color={NEXT_DAY.accent} />
              nästa dag
            </LegendItem>
            <LegendItem>
              <LegendSwatch color={URGENCY.over.accent} dim />
              försenat
            </LegendItem>
          </Legend>
        </Toolbar>

        <Grid cardCols={cardCols} cardRows={safeCardRows}>
          {visibleRows.map((r) => {
            const mins = minsLeft(r.startklar, now);
            const key = urgency(mins);
            const u = r.nextDay ? NEXT_DAY : URGENCY[key];
            const label = leftLabel(mins);
            return (
              <Card
                accent={u.accent}
                glow={!r.nextDay && (key === 'now' || key === 'soon')}
                key={r.assignmentId}
                over={!r.nextDay && key === 'over'}
              >
                <CardTop>
                  <Plate value={r.regnr} />
                  {r.typ && <TypePill>{r.typ}</TypePill>}
                </CardTop>

                <Modell>{r.modell || ' '}</Modell>

                <Meta>
                  {/* Från/till på två rader när alla anläggningar visas —
                      på en rad får de inte plats. */}
                  {facility === ALL_FACILITIES && r.facility ? (
                    <>
                      <MetaRow>
                        <FontAwesomeIcon
                          color="rgba(255,255,255,.4)"
                          fontSize={14}
                          icon={faLocationDot}
                        />
                        <RouteTo>
                          <RouteFrom>{r.facility}</RouteFrom>
                        </RouteTo>
                      </MetaRow>
                      <MetaRow>
                        <RouteArrowSlot>→</RouteArrowSlot>
                        <RouteTo>{r.till}</RouteTo>
                        <ArrivalTime>framme {fmtClock(r.framme)}</ArrivalTime>
                      </MetaRow>
                    </>
                  ) : (
                    <MetaRow>
                      <FontAwesomeIcon
                        color="rgba(255,255,255,.4)"
                        fontSize={14}
                        icon={faLocationDot}
                      />
                      <RouteTo>{r.till}</RouteTo>
                      <ArrivalTime>framme {fmtClock(r.framme)}</ArrivalTime>
                    </MetaRow>
                  )}
                  {/* Fälttestaren som kör uppdraget — ratt-ikonen skiljer
                      FT:n från bokaren (person-ikon) nedanför. */}
                  {r.ft && (
                    <MetaRow>
                      <SubIcon
                        color="rgba(255,255,255,.4)"
                        fontSize={12}
                        icon={faDharmachakra}
                        title="Fälttestare"
                      />
                      <SubText>{r.ft}</SubText>
                    </MetaRow>
                  )}
                  {r.ftTel && (
                    <MetaRow>
                      <SubIcon
                        color="rgba(255,255,255,.4)"
                        fontSize={12}
                        icon={faPhone}
                        title="Fälttestarens telefon"
                      />
                      <SubText>{r.ftTel}</SubText>
                    </MetaRow>
                  )}
                  {r.bokare && (
                    <MetaRow>
                      <SubIcon
                        color="rgba(255,255,255,.4)"
                        fontSize={12}
                        icon={faUser}
                        title="Bokare"
                      />
                      <SubText>{r.bokare}</SubText>
                    </MetaRow>
                  )}
                  {r.bokareTel && (
                    <MetaRow>
                      <SubIcon
                        color="rgba(255,255,255,.4)"
                        fontSize={12}
                        icon={faPhone}
                      />
                      <SubText>{r.bokareTel}</SubText>
                    </MetaRow>
                  )}
                  {r.foretag && (
                    <MetaRow>
                      <SubIcon
                        color="rgba(255,255,255,.4)"
                        fontSize={12}
                        icon={faBuilding}
                      />
                      <SubText>{r.foretag}</SubText>
                    </MetaRow>
                  )}
                  {r.info && <InfoRow>{r.info}</InfoRow>}
                </Meta>

                {(r.keyHandedIn || showTruckBadge(r)) && (
                  <BadgeRow>
                    {r.keyHandedIn && (
                      <TruckBadge title="Nyckel inlämnad">
                        <FontAwesomeIcon
                          color="rgba(255,255,255,.6)"
                          fontSize={12}
                          icon={faKey}
                        />
                      </TruckBadge>
                    )}
                    {showTruckBadge(r) && (
                      <TruckBadge
                        title={
                          r.status ===
                          AssignmentStatusEnum.AssignedToCarTransportTruck
                            ? 'Tilldelad lastbil'
                            : 'Ska på lastbil'
                        }
                      >
                        <FontAwesomeIcon
                          color="rgba(255,255,255,.6)"
                          fontSize={12}
                          icon={faTruck}
                        />
                        Lastbil
                      </TruckBadge>
                    )}
                  </BadgeRow>
                )}

                <CardBottom>
                  <StartklarCol>
                    <StartklarLabel>
                      {r.nextDay ? 'Nästa dag' : 'Startklar'}
                    </StartklarLabel>
                    <StartklarTime>{fmtClock(r.startklar)}</StartklarTime>
                  </StartklarCol>
                  <LeftCol color={u.color}>
                    <LeftBig>{label.big}</LeftBig>
                    <LeftSmall>{label.small}</LeftSmall>
                  </LeftCol>
                </CardBottom>
              </Card>
            );
          })}
          {overflowCount > 0 && (
            <OverflowCell>
              <span>+{overflowCount} fler</span>
              <span>
                {overflowNextDayOnly
                  ? 'nästa dag'
                  : isToday
                    ? 'senare idag'
                    : 'senare'}
              </span>
            </OverflowCell>
          )}
          {visibleRows.length === 0 && (
            <EmptyAll>
              {assignmentsCall.response
                ? isToday
                  ? 'Inget att förbereda just nu.'
                  : 'Inget att förbereda denna dag.'
                : 'Laddar…'}
            </EmptyAll>
          )}
        </Grid>
      </Canvas>
    </Stage>
  );
};

export default StartklarPage;
